import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../../firebase";
import PageWrapper from "../../components/PageWrapper";
import ToolbarHeader from "../Community/components/ToolbarHeader";
// import "../Community/css/ui-shell.css"; // shared

export default function TipsTricksIndex() {
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("q") || "");

  // ---- Firestore (real-time) ----
  useEffect(() => {
    const qTips = query(collection(db, "tips_tricks"), orderBy("createdAt", "desc"));
    const un = onSnapshot(
      qTips,
      (snap) => {
        const rows = snap.docs.map((d) => mapTipDoc(d.id, d.data()));
        setTips(rows);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => un();
  }, []);

  // ---- Sync URL (?q) ----
  useEffect(() => {
    const next = {};
    if (search) next.q = search;
    setSearchParams(next, { replace: true });
  }, [search, setSearchParams]);

  // ---- Derived (search only; newest first) ----
  const list = useMemo(() => {
    let src = tips;
    if (search.trim()) {
      const q = search.toLowerCase();
      src = src.filter((x) =>
        [x.title, x.summary || "", x.authorName || ""]
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }
    // ensure newest first (fallback if createdAt missing)
    const copy = [...src];
    copy.sort((a, b) => (b.createdAtMs || 0) - (a.createdAtMs || 0));
    return copy;
  }, [tips, search]);

return (
  <PageWrapper>
    <div className="page" id="tips">
      <ToolbarHeader
        title="GTA VI — Tips & Tricks"
        search={search}
        setSearch={setSearch}
        placeholder="Search tips…"
        className="vw-toolbar"
      />

      {/* Loading skeleton */}
      {loading && (
        <ul className="card-grid sc-gallery vw-gallery">
          {Array.from({ length: 8 }).map((_, i) => (
            <li key={`sk-${i}`} className="card sc-card vw-card">
              <div className="sc-thumb vw-thumb skeleton" />
              <div className="sc-name skeleton skeleton-text" />
              <div className="sc-subtle vw-subline">
                <span className="chip skeleton skeleton-chip" />
                <span className="chip skeleton skeleton-chip" />
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Collection empty (no docs in Firestore) */}
      {!loading && tips.length === 0 && (
        <div className="empty">No Tips & Tricks available. Please stay tuned.</div>
      )}

      {/* Results */}
      {!loading && tips.length > 0 && (
        <ul className="card-grid sc-gallery vw-gallery">
          {list.map((tip) => (
            <li key={tip.id} className="card sc-card vw-card">
              <Link to={`/community/tips_tricks/${tip.slug || tip.id}`} className="card-link sc-link">
                <div className="sc-thumb vw-thumb">
                  {tip.cover ? (
                    <img className="sc-img vw-img" src={tip.cover} alt={`${tip.title} cover`} loading="lazy" />
                  ) : (
                    <div className="sc-img sc-img--placeholder vw-placeholder" aria-label="No image">💡</div>
                  )}
                </div>

                <div className="sc-name vw-title" title={tip.title}>
                  {tip.title}
                </div>

                <div className="sc-subtle vw-subline">
                  {tip.authorName && <span className="chip">👤 {tip.authorName}</span>}
                  {tip.createdAtText && <span className="chip">{tip.createdAtText}</span>}
                  <span className="chip">👍 {tip.reactions?.like || 0}</span>
                  <span className="chip">🔥 {tip.reactions?.fire || 0}</span>
                </div>
              </Link>
            </li>
          ))}

          {/* Search-only empty state (docs exist but none match query) */}
          {list.length === 0 && (
            <li className="card sc-card vw-card" style={{ gridColumn: "1 / -1" }}>
              <div className="sc-thumb vw-thumb sc-img--placeholder vw-placeholder" aria-label="No results">
                🔎
              </div>
              <div className="sc-name vw-title">No tips match your search</div>
              <div className="sc-subtle vw-subline">
                <span className="chip">Try a different keyword</span>
              </div>
            </li>
          )}
        </ul>
      )}
    </div>
  </PageWrapper>
);

}

/* -------- helpers -------- */

function mapTipDoc(id, x = {}) {
  const createdAtMs =
    x.createdAt?.toMillis?.() ??
    (typeof x.createdAt === "number" ? x.createdAt : 0);

  const cover =
    x.coverImage ||
    (Array.isArray(x.gallery) && x.gallery.length > 0 ? x.gallery[0] : "") ||
    x.image ||
    x.imageUrl ||
    "";

  const score = (x.reactions?.like || 0) + (x.reactions?.fire || 0) + (x.reactions?.wow || 0);

  return {
    id,
    slug: x.slug || null,
    title: x.title || "Untitled Tip",
    summary: x.summary || "",
    cover,
    // tags intentionally not shown in UI
    authorName: x.authorName || "",
    createdAtMs,
    createdAtText: createdAtMs ? new Date(createdAtMs).toLocaleDateString() : "",
    reactions: x.reactions || {},
    score,
  };
}
